import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Subscription = ({ currentUserId, appMode }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUserId || (appMode !== 'Agent' && appMode !== 'Private Owner')) {
      navigate('/marketplace');
      return;
    }
    fetchUserStats();
  }, [currentUserId, appMode]);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subscriptions/user/${currentUserId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan) => {
    try {
      await axios.post(`${API}/subscriptions`, {
        userId: currentUserId,
        userMode: appMode,
        plan: plan,
        isTrial: false
      });
      toast.success('Subscription activated successfully!');
      fetchUserStats();
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to activate subscription');
    }
  };

  const handleStartTrial = async (plan) => {
    try {
      await axios.post(`${API}/subscriptions`, {
        userId: currentUserId,
        userMode: appMode,
        plan: plan,
        isTrial: true
      });
      toast.success('1-month free trial activated!');
      fetchUserStats();
      navigate('/new-listing');
    } catch (error) {
      console.error('Error starting trial:', error);
      toast.error('Failed to start trial');
    }
  };

  const formatZAR = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
          <p className="mt-4 text-gray-600">Loading subscription information...</p>
        </div>
      </main>
    );
  }

  // SUBSCRIPTION PLANS - DIFFERENT FOR AGENTS AND PRIVATE OWNERS
  const ownerPlans = [
    { id: 'owner_1', name: '1-Property Plan', price: 100, limit: 1 },
    { id: 'owner_3', name: '3-Property Plan', price: 299, limit: 3 }
  ];

  const agentPlans = [
    { id: 'agent_15', name: '15-Listing Plan', price: 350, limit: 15 },
    { id: 'agent_30', name: '30-Listing Plan', price: 550, limit: 30 },
    { id: 'agent_100', name: '100-Listing Plan', price: 2500, limit: 100 }
  ];

  const plans = appMode === 'Agent' ? agentPlans : ownerPlans;
  const hasActiveSubscription = stats?.subscriptionStatus === 'active' || stats?.subscriptionStatus === 'trial';

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
        Subscription Plans
      </h2>

      {/* Current Status */}
      {hasActiveSubscription && (
        <Card className="mb-8 bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-700 flex items-center">
              <i className="fas fa-check-circle mr-2"></i> Active Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-bold text-lg">
                  {stats.isTrial ? '1-Month Free Trial' : 'Active'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Properties Listed</p>
                <p className="font-bold text-lg">
                  {stats.propertiesListed} / {stats.propertyLimit}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Expires On</p>
                <p className="font-bold text-lg">
                  {new Date(stats.subscriptionEndDate).toLocaleDateString()}
                </p>
              </div>
            </div>
            {!stats.canListMore && (
              <p className="mt-4 text-red-600 font-semibold">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                You've reached your property limit. Upgrade your plan to list more properties.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Free Trial Offer */}
      {!hasActiveSubscription && (
        <Card className="mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-purple-700 mb-2">
                <i className="fas fa-gift mr-2"></i> Start Your 1-Month Free Trial!
              </h3>
              <p className="text-gray-700 mb-4">
                Choose any plan and enjoy full access for 1 month - completely FREE!
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map(plan => (
          <Card key={plan.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardTitle className="text-2xl text-emerald-700">{plan.name}</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <p className="text-4xl font-extrabold text-gray-800 mb-2">
                  {formatZAR(plan.price)}
                  <span className="text-lg text-gray-600 font-normal"> / month</span>
                </p>
                <p className="text-gray-600">List up to {plan.limit} {plan.limit === 1 ? 'property' : 'properties'}</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm">
                  <i className="fas fa-check text-emerald-600 mr-2"></i>
                  <span>Full access to all features</span>
                </div>
                <div className="flex items-center text-sm">
                  <i className="fas fa-check text-emerald-600 mr-2"></i>
                  <span>Unlimited photo & video uploads</span>
                </div>
                <div className="flex items-center text-sm">
                  <i className="fas fa-check text-emerald-600 mr-2"></i>
                  <span>Receive guest inquiries</span>
                </div>
                <div className="flex items-center text-sm">
                  <i className="fas fa-check text-emerald-600 mr-2"></i>
                  <span>Launch marketing campaigns</span>
                </div>
                {!hasActiveSubscription && (
                  <div className="flex items-center text-sm font-semibold text-purple-600">
                    <i className="fas fa-gift mr-2"></i>
                    <span>FREE for first month</span>
                  </div>
                )}
              </div>

              {!hasActiveSubscription ? (
                <Button
                  onClick={() => handleStartTrial(plan.id)}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid={`trial-${plan.id}-btn`}
                >
                  Start Free Trial
                </Button>
              ) : stats.subscriptionStatus === 'trial' ? (
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  Subscribe After Trial
                </Button>
              ) : (
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={stats.subscriptionStatus === 'active'}
                  data-testid={`subscribe-${plan.id}-btn`}
                >
                  {stats.subscriptionStatus === 'active' ? 'Current Plan' : 'Upgrade Plan'}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 bg-gray-50 p-6 rounded-lg">
        <h3 className="font-semibold text-lg mb-3">Subscription Information</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li><i className="fas fa-info-circle text-emerald-600 mr-2"></i> All subscriptions include a 1-month free trial for new users</li>
          <li><i className="fas fa-info-circle text-emerald-600 mr-2"></i> Monthly billing - subscriptions renew every 30 days</li>
          <li><i className="fas fa-info-circle text-emerald-600 mr-2"></i> Influencers and Guests have free access - no subscription required</li>
          <li><i className="fas fa-info-circle text-emerald-600 mr-2"></i> You can upgrade or change your plan at any time</li>
        </ul>
      </div>
    </main>
  );
};

export default Subscription;
